import { useState, useMemo } from 'react';
import { User as UserIcon, Mail, Calendar, Clock, Check, X, Edit2, Hash, Star } from 'lucide-react';
import StarRating from '@/components/StarRating';
import type { Booking, HolidayHome, Review } from '../types';
import { useAppStore } from '../store';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const ProfilePage = () => {
  const { currentUser, bookings, homes, updateUser, addReview, getReviewForBooking } = useAppStore();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editName, setEditName] = useState(currentUser?.name || '');
  const [saving, setSaving] = useState(false);

  // Review modal state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewBooking, setReviewBooking] = useState<Booking | null>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const userBookings = useMemo(() => {
    if (!currentUser) return [];
    return bookings
      .filter(booking => booking.userId === currentUser.id)
      .sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }, [bookings, currentUser]);

  const upcomingBookings = useMemo(() => {
    const now = new Date();
    return userBookings.filter(booking =>
      booking.startDate > now && booking.status !== 'cancelled'
    );
  }, [userBookings]);

  const pastBookings = useMemo(() => {
    const now = new Date();
    return userBookings.filter(booking => booking.endDate < now);
  }, [userBookings]);

  const stats = useMemo(() => {
    const totalBookings = userBookings.length;
    const approvedBookings = userBookings.filter(b => b.status === 'approved').length;
    const totalNights = userBookings
      .filter(b => b.status !== 'cancelled')
      .reduce((acc, booking) => {
        const nights = Math.ceil(
          (booking.endDate.getTime() - booking.startDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        return acc + nights;
      }, 0);
    return [
      { label: 'Buchungen', value: totalBookings },
      { label: 'Bestätigt', value: approvedBookings },
      { label: 'Nächte', value: totalNights },
    ];
  }, [userBookings]);

  const handleEditName = async () => {
    if (!currentUser || !editName.trim()) return;
    setSaving(true);
    try {
      await updateUser(currentUser.id, { name: editName.trim() });
      setEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update name:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenReviewModal = (booking: Booking) => {
    setReviewBooking(booking);
    setReviewRating(5);
    setReviewComment('');
    setReviewError(null);
    setReviewModalOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewBooking || !currentUser) return;

    setSubmittingReview(true);
    setReviewError(null);

    const result = await addReview({
      homeId: reviewBooking.homeId,
      bookingId: reviewBooking.id,
      userId: currentUser.id,
      rating: reviewRating,
      comment: reviewComment,
    });

    if (result.success) {
      setReviewModalOpen(false);
      setReviewBooking(null);
    } else {
      setReviewError(result.error || 'Failed to submit review');
    }

    setSubmittingReview(false);
  };

  const getRoleBadge = () => {
    if (currentUser?.isAdmin) return { label: 'Admin', variant: 'default' as const };
    if (currentUser?.isFamily) return { label: 'Familie', variant: 'secondary' as const };
    return { label: 'Gast', variant: 'outline' as const };
  };

  if (!currentUser) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardContent className="p-12 text-center">
            <h3 className="text-lg font-semibold mb-2">Nicht eingeloggt</h3>
            <p className="text-muted-foreground">Bitte melde dich an, um dein Profil zu sehen.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const role = getRoleBadge();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Profile Header */}
      <section className="mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserIcon className="w-10 h-10 text-primary" />
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">{currentUser.name}</h1>
                  <Badge variant={role.variant}>{role.label}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditName(currentUser.name);
                      setEditModalOpen(true);
                    }}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Mail className="h-4 w-4" />
                  <span>{currentUser.email}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Badge variant={currentUser.status === 'approved' ? 'default' : 'secondary'}>
                    {currentUser.status}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Stats Section */}
      <section className="mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} className="hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-primary mb-1">{stat.value}</div>
                <div className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">
                  {stat.label}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Separator className="my-8" />

      {/* Bookings Section */}
      <section>
        <h2 className="text-2xl font-semibold mb-6">Meine Buchungen</h2>

        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="upcoming">
              Bevorstehend ({upcomingBookings.length})
            </TabsTrigger>
            <TabsTrigger value="past">
              Vergangen ({pastBookings.length})
            </TabsTrigger>
            <TabsTrigger value="all">
              Alle ({userBookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            <BookingsTable bookings={upcomingBookings} homes={homes} />
          </TabsContent>

          <TabsContent value="past">
            <BookingsTable
              bookings={pastBookings}
              homes={homes}
              showReviewPrompt
              onLeaveReview={handleOpenReviewModal}
              getReviewForBooking={getReviewForBooking}
            />
          </TabsContent>

          <TabsContent value="all">
            <BookingsTable bookings={userBookings} homes={homes} />
          </TabsContent>
        </Tabs>
      </section>

      {/* Edit Name Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Profil bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Dein Name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleEditName} disabled={saving || !editName.trim()}>
              {saving ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Bewertung schreiben</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {reviewBooking && (
              <div className="text-sm text-muted-foreground">
                Bewertung für{' '}
                <span className="font-medium text-foreground">
                  {homes.find(h => h.id === reviewBooking.homeId)?.name}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label>Bewertung</Label>
              <div className="flex items-center gap-4">
                <StarRating
                  rating={reviewRating}
                  size="lg"
                  interactive
                  onChange={setReviewRating}
                />
                <span className="text-lg font-semibold">{reviewRating}/5</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reviewComment">Kommentar (optional)</Label>
              <textarea
                id="reviewComment"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Erfahrungen teilen..."
                className="flex min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              />
            </div>

            {reviewError && (
              <div className="text-sm p-3 rounded-md bg-red-50 text-red-700 border border-red-200">
                {reviewError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModalOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSubmitReview} disabled={submittingReview}>
              {submittingReview ? 'Wird gesendet...' : 'Bewertung absenden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface BookingsTableProps {
  bookings: Booking[];
  homes: HolidayHome[];
  showReviewPrompt?: boolean;
  onLeaveReview?: (booking: Booking) => void;
  getReviewForBooking?: (bookingId: string) => Review | undefined;
}

const BookingsTable: React.FC<BookingsTableProps> = ({ bookings, homes, showReviewPrompt, onLeaveReview, getReviewForBooking }) => {
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'pending': return 'secondary';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  if (bookings.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Keine Buchungen gefunden</h3>
          <p className="text-muted-foreground">
            {showReviewPrompt
              ? 'Noch keine abgeschlossenen Aufenthalte.'
              : 'Keine bevorstehenden Buchungen.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Referenz</TableHead>
            <TableHead>Unterkunft</TableHead>
            <TableHead>Daten</TableHead>
            <TableHead>Nächte</TableHead>
            <TableHead>Preis</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Zahlung</TableHead>
            {showReviewPrompt && <TableHead>Bewertung</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((booking) => {
            const home = homes.find(h => h.id === booking.homeId);
            const nights = Math.ceil(
              (booking.endDate.getTime() - booking.startDate.getTime()) / (1000 * 60 * 60 * 24)
            );

            return (
              <TableRow key={booking.id}>
                <TableCell>
                  <div className="flex items-center gap-1 font-mono text-sm">
                    <Hash size={14} className="text-muted-foreground" />
                    {booking.referenceNumber || '-'}
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  {home?.name || 'Unbekannte Unterkunft'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {booking.startDate.toLocaleDateString()} - {booking.endDate.toLocaleDateString()}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    {nights}
                  </div>
                </TableCell>
                <TableCell>
                  CHF {booking.totalPrice.toLocaleString()}
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusVariant(booking.status)}>
                    {booking.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {booking.isPaid ? (
                    <Badge variant="default" className="bg-green-600">
                      <Check className="h-3 w-3 mr-1" />
                      Bezahlt
                    </Badge>
                  ) : (
                    <Badge variant="outline">
                      <X className="h-3 w-3 mr-1" />
                      Ausstehend
                    </Badge>
                  )}
                </TableCell>
                {showReviewPrompt && (
                  <TableCell>
                    {booking.status === 'approved' && (
                      (() => {
                        const existingReview = getReviewForBooking?.(booking.id);
                        if (existingReview) {
                          return (
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="text-sm">{existingReview.rating}</span>
                            </div>
                          );
                        }
                        return (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onLeaveReview?.(booking)}
                          >
                            <Star className="h-3 w-3 mr-1" />
                            Review
                          </Button>
                        );
                      })()
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};

export default ProfilePage;
